<script setup lang="ts">
import { computed } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'

interface Product {
  id: number
  name: string
  image: string
  link: string
  desc: string
}

interface ProductGroup {
  name: string
  link: string
  desc: string
  products: Product[]
}

interface AuthorContext {
  id: number
  last_name: string
  first_name: string
  patronymic: string
  desc: string
  profile_image_url: string
  bio_link: string
  product_groups: ProductGroup[]
}

const ctx = usePageContext<AuthorContext>()
const { isMobile } = useScreen()

const fullName = computed(() => {
  return [ctx.last_name, ctx.first_name, ctx.patronymic].filter(Boolean).join(' ')
})

const breadcrumbs = computed(() => [
  { label: `${fullName.value} (визитка)`, href: ctx.bio_link },
  { label: 'Страница автора' }
])
</script>

<template>
  <div class="account-frame">
    <app-navigation role="teacher" rules-url="/website/documents/oferta" />

    <div v-cloak class="teacher-author content">
      <v-notifications position="bottom right" />

      <v-breadcrumbs v-if="!isMobile" class="mb-1" :items="breadcrumbs" />

      <h1 class="header mb-2">Страница автора</h1>

      <div class="teacher-author__info">
        <img class="teacher-author__avatar" :src="ctx.profile_image_url" />

        <h2 class="teacher-author__fullname" v-text="fullName" />

        <div class="teacher-author__description" v-text="ctx.desc" />
      </div>

      <ul class="teacher-author__products-container">
        <section v-for="group in ctx.product_groups" :key="group.name" class="teacher-author__product-group">
          <v-link class="text-title-bold mb-2" mode="primary" :href="group.link" target="_blank">
            {{ group.name }}
          </v-link>

          <p class="mb-3" v-text="group.desc" />

          <ul class="teacher-author__products">
            <div v-for="product in group.products" :key="product.id" class="teacher-author__product">
              <v-link mode="primary" class="mb-2" :href="product.link" target="_blank">
                {{ product.name }}
              </v-link>

              <img class="teacher-author__product-image mb-1" :src="`/teacher/product_image/${product.id}`" />

              <p v-text="product.desc" />
            </div>
          </ul>
        </section>
      </ul>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/teacher/author.scss' as *;
</style>
